// Pestaña de líneas del pedido.
//
// El selector Pendientes / Facturadas es excluyente: las líneas tienen pestaña
// propia y el contador de cada grupo está siempre a la vista, así que nada queda
// oculto — se elige qué se está mirando. Cada grupo conserva su juego de
// columnas y ninguna etiqueta significa dos cosas (ver `columnasLineas`).
//
// SEMÁNTICA DE LOS FILTROS: dentro de uno, OR —dos artículos son «este o
// este»—; entre filtros distintos, AND. Es lo que permite «estas tres
// referencias, de estas dos facturas» sin que el resultado sea vacío.
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ColumnsMenu } from '@/components/common/ColumnsMenu'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import type { ResumenLinea } from '@/utils/cargasPicking'
import type { ResumenAlbaranes } from '@/utils/albaranesLinea'
import type { PedidoVentaLinea } from '@/types'
import { TablaLineas, ordenarLineas, type OrdenTabla } from './TablaLineas'
import { TablaLineasAgrupadas, ordenarGrupos } from './TablaLineasAgrupadas'
import { columnasFacturadas, columnasPendientes } from './columnasLineas'
import { columnasHijas, columnasPadre, type DatosCarga } from './columnasAgrupadas'
import { agruparLineas, clavesDeFilaDe, marcasDeLinea } from '@/utils/lineasAgrupadas'
import { claveFila } from '@/utils/marcasFilas'
import { MultiFiltro, type OpcionMulti } from '@/components/common/MultiFiltro'
import { etiquetaCatalogoDe } from '@/utils/filtroArticulo'
import { BarraMarcas } from '@/components/common/MarcasFilas'
import type { CargaLinea, MarcasFilas } from '@/types'
import {
  BarraFiltrosActivos, descripcionDe, filtrosDe, type FiltroActivo,
} from '@/components/common/BarraFiltrosActivos'
import { campoCls, segmentoCls } from './controles'
import type { FiltroConCarga, GrupoLineas } from './useTabsPedido'

/** Los filtros de la sección. Es el subconjunto de `useTabsPedido` que le toca:
 *  se pasa entero para no desfilar doce props, y porque los seis se limpian a la
 *  vez. */
export interface FiltrosLineas {
  articulos: string[]
  albaranes: string[]
  cargas: string[]
  destinos: string[]
  conCarga: FiltroConCarga
  familia: string
  estado: string
  facturas: string[]
  setArticulos: (v: string[]) => void
  setAlbaranes: (v: string[]) => void
  setCargas: (v: string[]) => void
  setDestinos: (v: string[]) => void
  setConCarga: (v: FiltroConCarga) => void
  setFamilia: (v: string) => void
  setEstado: (v: string) => void
  setFacturas: (v: string[]) => void
  limpiarFiltros: () => void
}

interface Props {
  dark: boolean
  sinFacturar: PedidoVentaLinea[]
  facturadas: PedidoVentaLinea[]
  pickingPorLinea: Map<string, ResumenLinea>
  lanzamientoDe: (l: PedidoVentaLinea) => { lanzado: number; pendiente: number; exceso: number }
  /** Los albaranes de una fila. Va a los DOS grupos: es la única columna
   *  compartida, y su regla vive en `albaranesLinea.ts`. */
  albaranesDe: (l: PedidoVentaLinea) => ResumenAlbaranes | undefined
  /** Las cargas del pedido. Sólo para el destino y la referencia de cliente de la
   *  pestaña «todas»: el NÚMERO de carga de cada entrega sale del albarán, no de
   *  aquí —esta lista son todas las cargas de la línea y no sabe cuál va con qué
   *  factura—. */
  cargas: CargaLinea[]
  grupo: GrupoLineas
  onGrupo: (g: GrupoLineas) => void
  /** Los filtros, que viven en la URL. No son estado de esta sección a propósito:
   *  cambiar de pestaña la desmonta, y rehacer el filtro cada vez era el coste
   *  real de guardarlos aquí. */
  filtros: FiltrosLineas
  /** Las facturas que existen en el pedido, para el desplegable. */
  facturas: string[]
  /** Marcas del usuario, con el pedido entero por ámbito. */
  marcas: MarcasFilas
}

export function SeccionLineas({
  dark, sinFacturar, facturadas, pickingPorLinea, lanzamientoDe, albaranesDe, cargas,
  grupo, onGrupo, filtros, facturas, marcas,
}: Props) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()

  const {
    articulos, albaranes: albaranesFiltro, cargas: cargasFiltro, destinos: destinosFiltro,
    conCarga, familia, estado, facturas: facturasFiltro,
    setArticulos, setAlbaranes, setCargas, setDestinos, setConCarga, setFamilia,
    setEstado, setFacturas, limpiarFiltros,
  } = filtros

  const [orden, setOrden] = useState<OrdenTabla | null>(null)
  const [ocultas, setOcultas] = useState<Set<string>>(new Set())
  // Las líneas abiertas viven aquí y no en la tabla: es lo que permite el
  // «desplegar todo», igual que hace Cargas con su propio control.
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set())

  const esFacturado = grupo === 'facturadas'
  const esTodas = grupo === 'todas'
  const esPendiente = grupo === 'pendientes'
  const base = esTodas ? [...sinFacturar, ...facturadas] : (esFacturado ? facturadas : sinFacturar)

  const todas = useMemo(() => [...sinFacturar, ...facturadas], [sinFacturar, facturadas])
  const familias = useMemo(
    () => [...new Set(todas.map(l => l.familia).filter(Boolean) as string[])].sort(),
    [todas],
  )
  const estados = useMemo(
    () => [...new Set(todas.map(l => l.estadoLinea).filter(Boolean) as string[])].sort(),
    [todas],
  )

  // Los artículos del grupo que se está mirando: ofrecer uno que no está aquí
  // sería prometer un resultado vacío.
  const opcionesArticulo: OpcionMulti[] = useMemo(() => {
    const vistos = new Map<string, string>()
    for (const l of base) {
      if (l.articulo && !vistos.has(l.articulo)) vistos.set(l.articulo, l.descripcion ?? '')
    }
    return [...vistos.entries()]
      .map(([codigo, descripcion]) => ({
        valor: codigo, titulo: fmtArt(codigo), detalle: descripcion,
      }))
      .sort((a, b) => a.titulo.localeCompare(b.titulo, 'es-ES'))
  }, [base, fmtArt])

  const opcionesFactura: OpcionMulti[] = useMemo(
    () => facturas.map(f => ({ valor: f, titulo: f })),
    [facturas],
  )

  // Albaranes y cargas del pedido, sacados de las propias filas: ofrecer uno que
  // no está aquí sería prometer un resultado vacío, igual que con los artículos.
  const opcionesAlbaran: OpcionMulti[] = useMemo(() => {
    const v = new Set<string>()
    for (const l of base) for (const a of albaranesDe(l)?.albaranes ?? []) v.add(a)
    return [...v].sort().map(a => ({ valor: a, titulo: a }))
  }, [base, albaranesDe])

  const opcionesCarga: OpcionMulti[] = useMemo(() => {
    const v = new Set<string>()
    for (const l of base) for (const c of albaranesDe(l)?.cargas ?? []) v.add(c)
    return [...v].sort().map(c => ({ valor: c, titulo: c }))
  }, [base, albaranesDe])

  // Destinos presentes. Sólo `avp` los tiene rellenos, así que fuera de ahí esta
  // lista viene vacía y el desplegable no se pinta.
  const opcionesDestino: OpcionMulti[] = useMemo(() => {
    const v = new Set<string>()
    for (const c of cargas) {
      const d = c.referenciaCliente?.trim()
      if (d) v.add(d)
    }
    return [...v].sort().map(d => ({ valor: d, titulo: d }))
  }, [cargas])

  /** Los destinos de una fila, vía sus cargas. */
  const destinosDe = useMemo(() => {
    const porCarga = new Map<string, string>()
    for (const c of cargas) {
      const d = c.referenciaCliente?.trim()
      if (d && !porCarga.has(c.carga)) porCarga.set(c.carga, d)
    }
    return (l: PedidoVentaLinea) =>
      (albaranesDe(l)?.cargas ?? []).map(c => porCarga.get(c)).filter(Boolean) as string[]
  }, [cargas, albaranesDe])

  // Coincidencia con el filtro de artículos: la usa la tabla para resaltar. Es
  // pertenencia al conjunto elegido, no una búsqueda por texto, así que dos
  // referencias resaltan las dos.
  const elegidos = useMemo(() => new Set(articulos), [articulos])
  const coincide = useMemo(
    () => (elegidos.size === 0
      ? null
      : (articulo?: string) => !!articulo && elegidos.has(articulo)),
    [elegidos],
  )

  const filtradas = useMemo(() => base.filter(l => {
    // AND entre filtros; OR dentro de cada uno.
    if (elegidos.size > 0 && !(l.articulo && elegidos.has(l.articulo))) return false
    if (familia && l.familia !== familia) return false
    if (estado && l.estadoLinea !== estado) return false
    // La factura ya no es exclusiva del grupo facturado: en «todas» las filas
    // facturadas conviven con las remanentes y el filtro sigue significando lo
    // mismo. En «pendientes» no hay ninguna, así que no se ofrece.
    if (!esPendiente && facturasFiltro.length > 0
        && !facturasFiltro.includes(l.factura?.trim() ?? '')) return false
    if (albaranesFiltro.length > 0) {
      const suyos = albaranesDe(l)?.albaranes ?? []
      if (!suyos.some(a => albaranesFiltro.includes(a))) return false
    }
    if (cargasFiltro.length > 0) {
      const suyas = albaranesDe(l)?.cargas ?? []
      if (!suyas.some(c => cargasFiltro.includes(c))) return false
    }
    if (destinosFiltro.length > 0
        && !destinosDe(l).some(d => destinosFiltro.includes(d))) return false
    if (conCarga) {
      // «Sin carga» aísla lo que NO salió por una carga: entregas manuales y
      // artículos no almacenables. Es lo que hoy no se puede separar de un
      // vistazo y lo que genera las preguntas del cliente.
      const tiene = (albaranesDe(l)?.cargas ?? []).length > 0
      if (conCarga === 'con' && !tiene) return false
      if (conCarga === 'sin' && tiene) return false
    }
    return true
  }), [base, elegidos, familia, estado, esPendiente, facturasFiltro,
       albaranesFiltro, cargasFiltro, destinosFiltro, conCarga, destinosDe, albaranesDe])

  const ctx = useMemo(
    () => ({ dark, fmtArt, t, coincide, albaranesDe }),
    [dark, fmtArt, t, coincide, albaranesDe],
  )

  // ── La pestaña «todas»: el grano cambia de (línea × factura) a LÍNEA ──────
  const grupos = useMemo(
    () => agruparLineas(filtradas, { albaranesDe }),
    [filtradas, albaranesDe],
  )

  /** Destino y referencia de cada carga. Constantes dentro de la carga, así que
   *  la primera fila que la trae ya vale. Sólo `avp` las tiene rellenas. */
  const datosCarga = useMemo(() => {
    const m = new Map<string, DatosCarga>()
    for (const c of cargas) {
      if (!m.has(c.carga)) {
        // OJO: van CRUZADAS respecto a su nombre en origen. `referenciaCliente`
        // es `cargacliente_custom`, que NO es un cliente: es el destino.
        m.set(c.carga, {
          destino: c.referenciaCliente?.trim() || undefined,
          referencia: c.referenciaCliente2?.trim() || undefined,
        })
      }
    }
    return (carga: string) => m.get(carga) ?? {}
  }, [cargas])

  const colsPadre = useMemo(() => columnasPadre(ctx), [ctx])
  const colsHijas = useMemo(() => columnasHijas(ctx, datosCarga), [ctx, datosCarga])
  const visiblesPadre = colsPadre.filter(c => !ocultas.has(c.key))
  const gruposOrdenados = useMemo(
    () => ordenarGrupos(grupos, colsPadre, orden),
    [grupos, colsPadre, orden],
  )

  /** Marcar una línea marca sus filas en las otras dos pestañas (ver
   *  `marcasDeLinea`). Se indexa por la PRIMERA clave hija, que es la que la
   *  tabla usa como clave de la fila agrupada. */
  const marcasLinea = useMemo(() => {
    // Indexado por la clave de la LÍNEA, que no es la de ninguna de sus filas. Una
    // clave que no esté aquí es la de una sublínea y se deja pasar tal cual.
    const porLinea = new Map<string, string[]>()
    for (const g of grupos) porLinea.set(claveFila(g.inventtransid), clavesDeFilaDe(g))
    return marcasDeLinea(marcas, clave => porLinea.get(clave) ?? [clave])
  }, [grupos, marcas])
  const columnas = useMemo(
    () => esFacturado
      ? columnasFacturadas(ctx)
      : columnasPendientes({ ...ctx, pickingPorLinea, lanzamientoDe }),
    [esFacturado, ctx, pickingPorLinea, lanzamientoDe],
  )
  const visibles = columnas.filter(c => !ocultas.has(c.key))
  const ordenadas = useMemo(
    () => ordenarLineas(filtradas, columnas, orden),
    [filtradas, columnas, orden],
  )

  // TODOS los filtros llevan chip, también los multi-selección: el campo sólo
  // enseña un contador —para no cambiar de tamaño—, así que sin esta franja no
  // habría forma de ver ni de quitar una selección concreta.
  const filtrosActivos: FiltroActivo[] = [
    ...filtrosDe(articulos, setArticulos, {
      campo: t('salesOrders.line.article'),
      etiquetaDe: etiquetaCatalogoDe(opcionesArticulo),
      tooltipDe: descripcionDe(opcionesArticulo),
      prefijo: 'a:',
    }),
    ...(familia
      ? [{ clave: 'fam', campo: t('common.family'), valor: familia, quitar: () => setFamilia('') }]
      : []),
    ...(estado
      ? [{
          clave: 'est', campo: t('common.status'),
          valor: t('salesOrders.status.' + estado, { defaultValue: estado }),
          quitar: () => setEstado(''),
        }]
      : []),
    ...(esPendiente
      ? []
      : filtrosDe(facturasFiltro, setFacturas, {
          campo: t('salesOrders.invoice.invoice'),
          prefijo: 'f:',
        })),
    ...filtrosDe(albaranesFiltro, setAlbaranes, {
      campo: t('salesOrders.line.packingSlip'),
      prefijo: 'alb:',
    }),
    ...filtrosDe(cargasFiltro, setCargas, {
      campo: t('salesOrders.detalle.cargaCol'),
      prefijo: 'cg:',
    }),
    ...filtrosDe(destinosFiltro, setDestinos, {
      campo: t('salesOrders.line.destino'),
      prefijo: 'dst:',
    }),
    ...(conCarga
      ? [{
          clave: 'concarga', campo: t('salesOrders.detalle.cargaCol'),
          valor: t(conCarga === 'con'
            ? 'salesOrders.detalle.soloConCarga'
            : 'salesOrders.detalle.soloSinCarga'),
          quitar: () => setConCarga(''),
        }]
      : []),
  ]

  const limpiar = limpiarFiltros

  // El orden y las columnas ocultas son propios de cada juego de columnas: al
  // cambiar de grupo se reinician para no arrastrar una clave que allí no existe.
  // LOS FILTROS NO: son del pedido, no del grupo, y borrarlos al cambiar de vista
  // obligaba a rehacerlos para comparar lo mismo en los dos sitios.
  function cambiarGrupo(g: GrupoLineas) {
    if (g === grupo) return
    setOrden(null)
    setOcultas(new Set())
    setAbiertas(new Set())
    onGrupo(g)
  }

  const alternarOrden = (key: string) => setOrden(o => (o?.key === key
    ? { key, dir: o.dir === 'asc' ? 'desc' as const : 'asc' as const }
    : { key, dir: 'asc' as const }))

  const alternarAbierta = (inventtransid: string) => setAbiertas(prev => {
    const s = new Set(prev)
    if (s.has(inventtransid)) s.delete(inventtransid); else s.add(inventtransid)
    return s
  })

  const campo = campoCls(dark)

  return (
    <div className="flex flex-col gap-2.5">
      {/* Grupos a la izquierda —qué se está mirando— y filtros a la derecha
          —cómo se acota—. Son dos decisiones distintas y no se mezclan. */}
      <div className="flex items-center gap-2 flex-wrap">
        <div role="tablist" className={`inline-flex rounded-lg border p-0.5 ${
          dark ? 'border-white/10 bg-white/[0.03]' : 'border-slate-200 bg-slate-100/70'
        }`}>
          {([
            ['todas', 'salesOrders.detalle.grupoTodas', grupos.length],
            ['pendientes', 'salesOrders.detalle.grupoPendientes', sinFacturar.length],
            ['facturadas', 'salesOrders.detalle.grupoFacturadas', facturadas.length],
          ] as const).map(([id, clave, n]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={grupo === id}
              onClick={() => cambiarGrupo(id)}
              className={segmentoCls(dark, grupo === id)}
            >
              {t(clave)}
              <span className="tabular-nums font-bold ml-1.5">{n}</span>
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <MultiFiltro
            dark={dark}
            opciones={opcionesArticulo}
            seleccion={articulos}
            onSeleccion={setArticulos}
            placeholder={t('salesOrders.detalle.filtrarArticulos')}
            className="w-[240px]"
          />
          <select value={familia} onChange={e => setFamilia(e.target.value)} className={campo}>
            <option value="">{t('common.allFamilies')}</option>
            {familias.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <select value={estado} onChange={e => setEstado(e.target.value)} className={campo}>
            <option value="">{t('common.allStatuses')}</option>
            {estados.map(e => (
              <option key={e} value={e}>{t('salesOrders.status.' + e, { defaultValue: e })}</option>
            ))}
          </select>
          {!esPendiente && facturas.length > 0 && (
            <MultiFiltro
              dark={dark}
              opciones={opcionesFactura}
              seleccion={facturasFiltro}
              onSeleccion={setFacturas}
              placeholder={t('salesOrders.detalle.filtrarFacturas')}
              className="w-[200px]"
            />
          )}
          {/* Albarán y carga sólo donde significan algo: en «todas» y en el grupo
              facturado. En pendientes no hay entrega que buscar. */}
          {!esPendiente && opcionesAlbaran.length > 0 && (
            <MultiFiltro
              dark={dark}
              opciones={opcionesAlbaran}
              seleccion={albaranesFiltro}
              onSeleccion={setAlbaranes}
              placeholder={t('salesOrders.detalle.filtrarAlbaranes')}
              className="w-[180px]"
            />
          )}
          {!esPendiente && opcionesCarga.length > 0 && (
            <MultiFiltro
              dark={dark}
              opciones={opcionesCarga}
              seleccion={cargasFiltro}
              onSeleccion={setCargas}
              placeholder={t('salesOrders.detalle.filtrarCargas')}
              className="w-[180px]"
            />
          )}
          {/* Sólo donde hay destinos que ofrecer: fuera de `avp` el campo viene
              vacío y un desplegable siempre vacío es ruido. */}
          {!esPendiente && opcionesDestino.length > 0 && (
            <MultiFiltro
              dark={dark}
              opciones={opcionesDestino}
              seleccion={destinosFiltro}
              onSeleccion={setDestinos}
              placeholder={t('salesOrders.detalle.filtrarDestinos')}
              className="w-[170px]"
            />
          )}
          {!esPendiente && (
            <select
              value={conCarga}
              onChange={e => setConCarga(e.target.value as FiltroConCarga)}
              className={campo}
            >
              <option value="">{t('salesOrders.detalle.cargaCualquiera')}</option>
              <option value="con">{t('salesOrders.detalle.soloConCarga')}</option>
              <option value="sin">{t('salesOrders.detalle.soloSinCarga')}</option>
            </select>
          )}
          {esTodas && grupos.some(g => g.tieneDesglose) && (
            <button
              type="button"
              onClick={() => setAbiertas(prev => (prev.size > 0
                ? new Set()
                : new Set(grupos.filter(g => g.tieneDesglose).map(g => g.inventtransid))))}
              className={campo}
            >
              {t(abiertas.size > 0
                ? 'salesOrders.detalle.colapsarTodo'
                : 'salesOrders.detalle.expandirTodo')}
            </button>
          )}
          <ColumnsMenu
            dark={dark}
            items={(esTodas ? colsPadre : columnas).map(c => ({ key: c.key, label: c.etiqueta }))}
            visible={new Set((esTodas ? visiblesPadre : visibles).map(c => c.key))}
            onToggle={(key) => {
              const s = new Set(ocultas)
              if (s.has(key)) s.delete(key); else s.add(key)
              setOcultas(s)
            }}
            btnLabel={t('salesOrders.detalle.columnas')}
            title={t('salesOrders.detalle.columnas')}
          />
        </div>
      </div>

      {/* Sin `resumen`: el pie de la tabla ya dice «4 de 52 líneas» a dos
          centímetros, y repetirlo aquí era la misma frase dos veces. */}
      {filtrosActivos.length > 0 && (
        <BarraFiltrosActivos dark={dark} filtros={filtrosActivos} onLimpiar={limpiar} />
      )}

      {/* Junto a la de filtros: las dos dicen «esto lo has puesto tú encima del
          dato», y ninguna de las dos engorda la barra de controles. */}
      <BarraMarcas dark={dark} marcas={marcas} />

      {esTodas ? (
        <TablaLineasAgrupadas
          dark={dark}
          grupos={gruposOrdenados}
          columnas={visiblesPadre}
          columnasHijas={colsHijas}
          totalSinFiltrar={grupos.length}
          orden={orden}
          onOrden={alternarOrden}
          abiertas={abiertas}
          onAbrir={alternarAbierta}
          coincide={coincide}
          marcas={marcasLinea}
        />
      ) : (
        <TablaLineas
          dark={dark}
          lineas={ordenadas}
          totalSinFiltrar={base.length}
          columnas={visibles}
          orden={orden}
          onOrden={alternarOrden}
          coincide={coincide}
          marcas={marcas}
        />
      )}
    </div>
  )
}
